<script setup>
import { ref, reactive, computed } from "vue";
import { useToast } from "vue-toastification";
import axios from "axios";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { validatePassword } from "@/utils/validation";
import ContactSupportDialog from "@/components/dialogs/ContactSupportDialog.vue";
const { t } = useI18n();
const router = useRouter();

const formData = reactive({
  password: "",
  password_confirmation: "",
});

const errors = reactive({
  password: "",
  password_confirmation: "",
});

const generalError = ref("");
const isLoading = ref(false);
const isContactSupportDialogOpen = ref(false);
const showPassword = ref(false);
const showConfirmPassword = ref(false);

// Password requirements tracking
const passwordRequirements = computed(() => ({
  length: formData.password.length >= 8,
  digit: /\d/.test(formData.password),
  uppercase: /[A-Z]/.test(formData.password),
  special: /[!@#$%^&*(),.?":{}|<>]/.test(formData.password),
  match: formData.password && formData.password_confirmation && formData.password === formData.password_confirmation
}));

const isFormValid = computed(() => {
  return (
    formData.password &&
    formData.password_confirmation &&
    passwordRequirements.value.length &&
    passwordRequirements.value.digit &&
    passwordRequirements.value.uppercase &&
    passwordRequirements.value.special &&
    passwordRequirements.value.match
  );
});

const validateRealTime = (field) => {
  // Clear general error when user starts typing
  if (generalError.value) {
    generalError.value = "";
  }
  
  // Clear specific field errors (we're now using visual feedback instead)
  if (field === 'password') {
    errors.password = !formData.password ? t("auth.password_required") : "";
  } else if (field === 'password_confirmation') {
    errors.password_confirmation = !formData.password_confirmation ? t("auth.confirm_password_required") : "";
  }
};

const validatePasswordField = (password) => {
  if (!password) {
    return t("auth.password_required");
  }
  return "";
};

const validatePasswordConfirmation = (password, confirmPassword) => {
  if (!confirmPassword) {
    return t("auth.confirm_password_required");
  }
  return "";
};

const validateForm = () => {
  // Simple validation - just check if fields are filled
  errors.password = !formData.password ? t("auth.password_required") : "";
  errors.password_confirmation = !formData.password_confirmation ? t("auth.confirm_password_required") : "";

  // Return true if form is valid (using the computed property that checks all requirements)
  return isFormValid.value;
};

const showContactSupportDialog = () => {
  isContactSupportDialogOpen.value = true;
};

const handleSubmit = async () => {
  if (!validateForm()) {
    return;
  }

  isLoading.value = true;
  const token = router.currentRoute.value.query.token;

  const email = router.currentRoute.value.query.email;
  
  const data = {
    password: formData.password,
    password_confirmation: formData.password_confirmation,
    token,
    email,
  };

  try {
    const response = await axios.post("api/reset-password", data);

    if (response.status === 200) {
      useToast().success(t("notifications.password_reset_success"));
      generalError.value = "";
      await router.push("/login");
    } else {
      useToast().error(t("notifications.default_error"));
    }
  } catch (error) {
    generalError.value = error.response?.data?.message || t("notifications.default_error");
    useToast().error(generalError.value);
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <div class="auth-container">
    <!-- Logo Section -->
    <div class="logo-section">
      <router-link to="/login">
        <img
          src="/images/logos/legalTorchLogo.svg"
          alt="Legal Torch Logo"
          class="logo clickable"
        />
      </router-link>
    </div>

    <!-- Main Content -->
    <div class="content-wrapper">
      <div class="main-content">
        <h2 class="form-title">{{ t("auth.reset_password") }}</h2>
        <p class="form-subtitle">
          {{ t("auth.reset_password_subtitle") }}
        </p>

        <form @submit.prevent="handleSubmit">
          <!-- Password Input -->
          <div class="input-group">
            <label class="input-label">{{ t("auth.password") }}*</label>
            <div class="password-container">
              <input
                v-model="formData.password"
                :type="showPassword ? 'text' : 'password'"
                class="form-input"
                :placeholder="t('auth.password')"
                :class="{ error: errors.password }"
                autocomplete="new-password"
                @input="validateRealTime('password')"
              />
              <button type="button" class="password-toggle" @click="showPassword = !showPassword">
                <img
                  v-if="!showPassword"
                  src="/images/icons/eye.svg"
                  alt="Show password"
                  class="eye-icon"
                />
                <img v-else src="/images/icons/eye-closed.svg" alt="Hide password" class="eye-icon" />
              </button>
            </div>
            <!-- Password Requirements -->
            <div v-if="formData.password" class="password-requirements">
              <div class="requirement" :class="{ valid: passwordRequirements.length }">
                <span class="requirement-icon">{{ passwordRequirements.length ? '✓' : '○' }}</span>
                {{ t("auth.password_requirement_length") }}
              </div>
              <div class="requirement" :class="{ valid: passwordRequirements.digit }">
                <span class="requirement-icon">{{ passwordRequirements.digit ? '✓' : '○' }}</span>
                {{ t("auth.password_requirement_digit") }}
              </div>
              <div class="requirement" :class="{ valid: passwordRequirements.uppercase }">
                <span class="requirement-icon">{{ passwordRequirements.uppercase ? '✓' : '○' }}</span>
                {{ t("auth.password_requirement_uppercase") }}
              </div>
              <div class="requirement" :class="{ valid: passwordRequirements.special }">
                <span class="requirement-icon">{{ passwordRequirements.special ? '✓' : '○' }}</span>
                {{ t("auth.password_requirement_special") }}
              </div>
            </div>
            <span v-if="errors.password && !formData.password" class="error-text">{{ errors.password }}</span>
          </div>

          <!-- Confirm Password Input -->
          <div class="input-group">
            <label class="input-label">{{ t("auth.confirm_password") }}*</label>
            <div class="password-container">
              <input
                v-model="formData.password_confirmation"
                :type="showConfirmPassword ? 'text' : 'password'"
                class="form-input"
                :placeholder="t('auth.confirm_password')"
                :class="{ error: errors.password_confirmation }"
                autocomplete="new-password"
                @input="validateRealTime('password_confirmation')"
              />
              <button type="button" class="password-toggle" @click="showConfirmPassword = !showConfirmPassword">
                <img
                  v-if="!showConfirmPassword"
                  src="/images/icons/eye.svg"
                  alt="Show password"
                  class="eye-icon"
                />
                <img v-else src="/images/icons/eye-closed.svg" alt="Hide password" class="eye-icon" />
              </button>
            </div>
            <!-- Password Match Feedback -->
            <div v-if="formData.password_confirmation" class="password-match-feedback">
              <div class="requirement" :class="{ valid: passwordRequirements.match }">
                <span class="requirement-icon">{{ passwordRequirements.match ? '✓' : '○' }}</span>
                {{ t("auth.passwords_match") }}
              </div>
            </div>
            <span v-if="errors.password_confirmation && !formData.password_confirmation" class="error-text">{{ errors.password_confirmation }}</span>
          </div>

          <!-- General Error Message -->
          <div v-if="generalError" class="general-error">
            <div class="error-icon">⚠️</div>
            <span>{{ generalError }}</span>
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            class="submit-btn"
            :disabled="!isFormValid"
            :class="{ loading: isLoading }"
          >
            <div v-if="isLoading" class="loading-content">
              <div class="spinner"></div>
            </div>
            <span v-else>{{ t("common.save") }}</span>
          </button>

          <!-- Bottom Links -->
          <div class="bottom-links">
            <router-link to="/login" class="link">
              {{ t("auth.remember_the_password") }}
            </router-link>
            <button type="button" class="support-link" @click="showContactSupportDialog">
              {{ t("auth.need_help") }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <ContactSupportDialog
      :is-dialog-open="isContactSupportDialogOpen"
      @close-dialog="isContactSupportDialogOpen = false"
    />
  </div>
</template>

<style scoped lang="scss">
.auth-container {
  min-height: 100vh;
  background-color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}

.logo-section {
  margin-bottom: 40px;
}

.logo {
  width: 256px;
  height: 23px;

  &.clickable {
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.8;
    }
  }
}

.content-wrapper {
  width: 100%;
  max-width: 430px;
  display: flex;
  justify-content: center;
}

.main-content {
  flex: 1;
  width: 100%;
  max-width: 430px;
}

.form-title {
  text-align: center;
  font-size: 48px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 12px;
}

.form-subtitle {
  text-align: center;
  font-size: 16px;
  color: #6b7280;
  margin-bottom: 32px;
  line-height: 1.5;
}

.input-group {
  margin-bottom: 16px;
}

.input-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
}

.password-container {
  position: relative;
}

.password-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.eye-icon {
  width: 20px;
  height: 20px;
}

.form-input {
  width: 100%;
  padding: 16px 16px;
  border: 1px solid #d1d5db;
  border-radius: 16px;
  font-size: 16px;
  transition: border-color 0.2s;
  height: 48px;

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: none;
  }

  &.error {
    border-color: #ef4444;
  }

  &::placeholder {
    color: #9ca3af;
  }
}

.password-requirements,
.password-match-feedback {
  margin-top: 8px;
  padding: 12px;
  background-color: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.requirement {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 4px;
  transition: color 0.2s ease;

  &:last-child {
    margin-bottom: 0;
  }

  &.valid {
    color: #059669;
  }
}

.requirement-icon {
  margin-right: 8px;
  font-size: 14px;
  font-weight: bold;
  width: 16px;
  text-align: center;
}

.error-text {
  color: #ef4444;
  font-size: 12px;
  margin-top: 4px;
  display: block;
}

.general-error {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  margin-bottom: 16px;
  color: #dc2626;
  font-size: 14px;

  .error-icon {
    font-size: 16px;
    flex-shrink: 0;
  }
}

.submit-btn {
  width: 100%;
  background-color: #d8ed84;
  color: #1f2937;
  border: none;
  border-radius: 16px;
  padding: 16px 14px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-bottom: 20px;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background-color: #c9e16f;
  }

  &:disabled {
    background-color: rgba(216, 237, 132, 0.4);
    color: rgba(31, 41, 55, 0.6);
    cursor: not-allowed;
    box-shadow: 0 0 0 1px rgba(216, 237, 132, 0.2);
  }

  &.loading {
    background-color: rgba(216, 237, 132, 0.4);
    color: rgba(31, 41, 55, 0.6);
  }

  .loading-content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .spinner {
    width: 20px;
    height: 20px;
    border: 2px solid transparent;
    border-top: 2px solid #9ca3af;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    flex-shrink: 0;
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.bottom-links {
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: center;
}

.link {
  color: #2563eb;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: color 0.2s ease;

  &:hover {
    color: #1d4ed8;
    text-decoration: underline;
  }
}

.support-link {
  background: none;
  border: none;
  color: #2563eb;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #1d4ed8;
    text-decoration: underline;
  }
}

@media (max-width: 768px) {
  .auth-container {
    padding: 24px 16px;
  }

  .logo {
    width: 200px;
    height: 18px;
  }

  .content-wrapper {
    width: 100%;
    max-width: 100%;
  }

  .main-content {
    width: 100%;
    max-width: 100%;
  }

  .form-title {
    font-size: 24px;
  }

  .form-subtitle {
    font-size: 14px;
  }
}
</style>
